import {
  useMtmKpiValue,
  useYearlyKpiValue,
  useReadableRoots,
  useDailyKpiValue,
  HistoricalDialogData,
  useMonthlyKpiValue,
  mapDailyKpiValuesToNodes,
  YearlyKpiValue
} from '@domain/expert-system'
import {FlexPage} from '@hconnect/common/components'
import {DataContentWrapper} from '@hconnect/common/containers'
import {useExpandedState, useExpertSystemHierarchy, usePlantViewState} from '@hconnect/common/hooks'
import {CardBox, PageContainer, useIsMobile} from '@hconnect/uikit'
import {Stack} from '@mui/material'
import {useMemo, useState, useCallback, useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import {NavigateFunction, useNavigate} from 'react-router'

import {
  ExpertSystemUsageActions,
  ExpertSystemUsageListBreadcrumbs,
  HistoricalKpiDialog
} from '../components'
import {FEATURE_NAMESPACE} from '../consts'
import {ExpertSystemUsageTableContainer, ExpertSystemUsageListContainer} from '../containers'
import {
  useExpertSystemEquipmentState,
  useExpertSystemUsageListNavigation,
  useYearState,
  handleError,
  useMonthState
} from '../hooks'
import {HierarchyTableNode} from '../types'
import {mergeReadableRootsWithHierarchy, mapAndFilterHierarchy, getAllItemIds} from '../utils'

export const PAGE_PARAMS = {
  PLANT_VIEW: 'plantView',
  YEAR: 'year',
  EQUIPMENT_TYPE: 'equipment-type',
  MONTH: 'month'
}

type Props = {
  titleSlot?: React.ReactNode
  inverted?: boolean
}

export const ExpertSystemUsageRatePage = ({titleSlot, inverted = false}: Props) => {
  const navigate: NavigateFunction = useNavigate()
  const isMobile = useIsMobile()
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const [selectedNodeMonth, setSelectedNodeMonth] = useState<HistoricalDialogData>(undefined)
  const {plantView, setPlantView} = usePlantViewState(PAGE_PARAMS.PLANT_VIEW)
  const {equipmentType, setEquipmentType} = useExpertSystemEquipmentState(
    PAGE_PARAMS.EQUIPMENT_TYPE
  )
  const [isEditMode, setIsEditMode] = useState(false)
  const [isMonthlyView, setIsMonthlyView] = useState(false)
  const {year, setYear} = useYearState(PAGE_PARAMS.YEAR, new Date().getFullYear())
  const {month} = useMonthState(PAGE_PARAMS.MONTH, new Date().getMonth() + 1)
  const {data: data, isLoading, isError, refetch} = useExpertSystemHierarchy()
  const {
    data: dataWithYearPeriod,
    isLoading: isYearPeriodLoading,
    isError: isYearPeriodError,
    refetch: refetchYearPeriod,
    error: yearlyKpiValueError
  } = useYearlyKpiValue(equipmentType, year)

  const {
    data: dataWithDailyPeriod,
    refetch: refetchDailyPeriod,
    isError: isDailyPeriodError,
    error: dailyKpiValueError,
    isLoading: isDailyPeriodLoading
  } = useDailyKpiValue(equipmentType, year, month, {
    enabled: isMonthlyView
  })

  const {
    data: dataWithMonthlyPeriod,
    refetch: refetchMonthlyPeriod,
    isError: isMonthlyPeriodError,
    error: monthlyKpiValueError,
    isLoading: isMonthlyPeriodLoading
  } = useMonthlyKpiValue(equipmentType, year, month, {
    enabled: isMonthlyView
  })

  useEffect(() => {
    if (yearlyKpiValueError) {
      handleError(yearlyKpiValueError, navigate)
    }
  }, [yearlyKpiValueError, navigate])

  const {
    data: mtmData,
    isLoading: isMonthsPeriodLoading,
    isError: isMonthsPeriodError,
    refetch: refetchMonthsPeriod,
    error: mtmKpiValueError
  } = useMtmKpiValue(equipmentType, year)

  const dataWithMonthsPeriod = mtmData?.columns
  const weightedAverages = mtmData?.weightedAverages

  useEffect(() => {
    if (mtmKpiValueError) {
      handleError(mtmKpiValueError, navigate)
    }
  }, [mtmKpiValueError, navigate])

  useEffect(() => {
    if (dailyKpiValueError) {
      handleError(dailyKpiValueError, navigate)
    }

    if (monthlyKpiValueError) {
      handleError(monthlyKpiValueError, navigate)
    }
  }, [dailyKpiValueError, monthlyKpiValueError, navigate])

  const {
    data: readableRootsData,
    isLoading: isReadableRootsLoading,
    isError: isReadableRootsError,
    refetch: refetchReadableRoots,
    error: readableRootsError
  } = useReadableRoots()

  useEffect(() => {
    if (readableRootsError) {
      handleError(readableRootsError, navigate)
    }
  }, [readableRootsError, navigate])

  const retryFunction = useCallback(() => {
    if (isError) void refetch()
    if (isReadableRootsError) void refetchReadableRoots()
    if (isYearPeriodError) void refetchYearPeriod()
    if (isMonthsPeriodError) void refetchMonthsPeriod()
    if (isDailyPeriodError) void refetchDailyPeriod()
    if (isMonthlyPeriodError) void refetchMonthlyPeriod()
  }, [
    isError,
    refetch,
    isReadableRootsError,
    refetchReadableRoots,
    isYearPeriodError,
    refetchYearPeriod,
    isMonthsPeriodError,
    refetchMonthsPeriod,
    isDailyPeriodError,
    refetchDailyPeriod,
    isMonthlyPeriodError,
    refetchMonthlyPeriod
  ])

  const hierarchy = useMemo<HierarchyTableNode[]>(() => {
    const availableData = mergeReadableRootsWithHierarchy(data, readableRootsData)
    return availableData
      ? mapAndFilterHierarchy(
          availableData,
          equipmentType,
          dataWithYearPeriod,
          dataWithMonthsPeriod,
          weightedAverages
        )
      : []
  }, [data, readableRootsData, equipmentType, dataWithYearPeriod, dataWithMonthsPeriod])

  const dailyHierarchy = useMemo<HierarchyTableNode[]>(() => {
    const availableData = mergeReadableRootsWithHierarchy(data, readableRootsData)
    if (!availableData?.length) return []

    const baseHierarchy = mapAndFilterHierarchy(
      availableData,
      equipmentType,
      dataWithMonthlyPeriod as unknown as YearlyKpiValue[],
      dataWithMonthsPeriod
    )

    return dataWithDailyPeriod?.length
      ? mapDailyKpiValuesToNodes(baseHierarchy, dataWithDailyPeriod)
      : baseHierarchy
  }, [
    data,
    readableRootsData,
    equipmentType,
    dataWithMonthlyPeriod,
    dataWithDailyPeriod,
    dataWithMonthsPeriod
  ])

  const allItemsIds = useMemo<string[]>(() => getAllItemIds('subItems', hierarchy), [hierarchy])

  const {getIsExpanded, toggleExpandCollapse, toggleExpandCollapseAll, allExpanded} =
    useExpandedState(allItemsIds)

  const {currentLocations, locationHistory, goToLocation, locationSiblings, enterLocation} =
    useExpertSystemUsageListNavigation(hierarchy)

  const getDataForTable = useCallback(() => {
    if (isMonthlyView) {
      return dailyHierarchy
    }
    return hierarchy
  }, [isMonthlyView, hierarchy, dailyHierarchy])

  return (
    <PageContainer>
      <>{!isMobile && titleSlot}</>
      <FlexPage
        headerActionContent={
          <Stack sx={{width: '100%'}} spacing={2}>
            {isMobile && (
              <ExpertSystemUsageListBreadcrumbs
                title={t('label.header')}
                locationHistory={locationHistory}
                goToLocation={goToLocation}
                locationSiblings={locationSiblings}
              />
            )}
            <ExpertSystemUsageActions
              toggleExpandCollapseAll={toggleExpandCollapseAll}
              allExpanded={allExpanded}
              plantView={plantView}
              setPlantView={setPlantView}
              equipmentType={equipmentType}
              setEquipmentType={setEquipmentType}
              year={year}
              setYear={setYear}
              isEditMode={isEditMode}
              setIsEditMode={setIsEditMode}
              isMonthlyView={isMonthlyView}
              setIsMonthlyView={setIsMonthlyView}
              month={month}
              inverted={inverted}
            />
          </Stack>
        }
        nameHeaderSx={{m: 0}}
        sxActionContent={{width: '100%'}}
      />
      <CardBox
        sx={{
          width: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          flex: 1
        }}
        data-test-id="expert-system-usage-rate-cardbox"
      >
        <DataContentWrapper
          showLoader={
            isLoading ||
            isReadableRootsLoading ||
            isYearPeriodLoading ||
            isMonthsPeriodLoading ||
            isDailyPeriodLoading ||
            isMonthlyPeriodLoading
          }
          isError={
            isError ||
            isReadableRootsError ||
            isYearPeriodError ||
            isMonthsPeriodError ||
            isDailyPeriodError ||
            isMonthlyPeriodError
          }
          data={getDataForTable()}
          retryFunction={retryFunction}
          progressSx={{color: 'primary.main'}}
        >
          {isMobile ? (
            <ExpertSystemUsageListContainer
              currentLocations={currentLocations}
              enterLocation={enterLocation}
            />
          ) : (
            <ExpertSystemUsageTableContainer
              isEditMode={isEditMode}
              data={getDataForTable()}
              plantView={plantView}
              getIsExpanded={getIsExpanded}
              toggleExpandCollapse={toggleExpandCollapse}
              equipmentType={equipmentType}
              year={year}
              setNodeMonth={setSelectedNodeMonth}
              isMonthlyView={isMonthlyView}
              date={new Date(Number(year), Number(month) - 1, 1)}
            />
          )}
        </DataContentWrapper>
      </CardBox>
      {selectedNodeMonth && (
        <HistoricalKpiDialog
          onClose={() => setSelectedNodeMonth(undefined)}
          kpiTargetEquipmentType={equipmentType}
          month={selectedNodeMonth.month}
          hierarchyId={selectedNodeMonth.hierarchyId}
          year={year}
          equipmentName={selectedNodeMonth.equipmentName}
        />
      )}
    </PageContainer>
  )
}
